// Minimal fetch wrapper for the /v1 API: cookie session, CSRF header on unsafe methods,
// structured errors (apiErrorSchema) and optional Idempotency-Key.
import type { ErrorCode } from "@maestros/contracts";

export const API_BASE = "/v1";
const UNSAFE = new Set(["POST", "PUT", "PATCH", "DELETE"]);

export class ApiRequestError extends Error {
  readonly status: number;
  readonly code: ErrorCode | "network" | "unknown";
  readonly retryable: boolean;
  readonly correlationId: string | null;
  readonly details: Record<string, unknown> | undefined;
  /** Raw response body, kept for endpoints that answer errors with a meaningful body. */
  readonly body: unknown;

  constructor(init: {
    status: number;
    code: ApiRequestError["code"];
    message: string;
    retryable: boolean;
    correlationId?: string | null;
    details?: Record<string, unknown>;
    body?: unknown;
  }) {
    super(init.message);
    this.name = "ApiRequestError";
    this.status = init.status;
    this.code = init.code;
    this.retryable = init.retryable;
    this.correlationId = init.correlationId ?? null;
    this.details = init.details;
    this.body = init.body;
  }
}

export interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  idempotencyKey?: string;
  signal?: AbortSignal;
  /** Extra request headers (e.g. the one-time `x-setup-token` of the first-run setup). */
  headers?: Record<string, string>;
}

export function buildUrl(path: string, query?: RequestOptions["query"]): string {
  const url = new URL(API_BASE + path, window.location.origin);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined || v === null || v === "") continue;
      url.searchParams.set(k, String(v));
    }
  }
  return url.pathname + url.search;
}

export function requestHeaders(method: string, idempotencyKey?: string, hasBody = false): Record<string, string> {
  const headers: Record<string, string> = { accept: "application/json" };
  if (hasBody) headers["content-type"] = "application/json";
  if (UNSAFE.has(method)) headers["x-requested-with"] = "maestros";
  if (idempotencyKey) headers["idempotency-key"] = idempotencyKey;
  return headers;
}

export async function toApiError(response: Response): Promise<ApiRequestError> {
  let payload: unknown = null;
  try {
    payload = await response.json();
  } catch {
    payload = null;
  }
  return parseErrorPayload(response.status, payload);
}

export function parseErrorPayload(status: number, payload: unknown): ApiRequestError {
  const err = (payload as { error?: Record<string, unknown> } | null)?.error;
  if (err && typeof err === "object") {
    return new ApiRequestError({
      status,
      code: (typeof err["code"] === "string" ? err["code"] : "unknown") as ApiRequestError["code"],
      message: typeof err["message"] === "string" ? err["message"] : `Request failed (${status}).`,
      retryable: err["retryable"] === true || status >= 500 || status === 429,
      correlationId: typeof err["correlation_id"] === "string" ? err["correlation_id"] : null,
      details: (err["details"] as Record<string, unknown> | undefined) ?? undefined,
    });
  }
  return new ApiRequestError({
    status,
    code: status === 503 ? "unavailable" : "unknown",
    message: `Request failed (${status}).`,
    retryable: status >= 500 || status === 429,
    body: payload,
  });
}

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? "GET";
  const hasBody = options.body !== undefined;
  let response: Response;
  try {
    response = await fetch(buildUrl(path, options.query), {
      method,
      credentials: "same-origin",
      headers: { ...requestHeaders(method, options.idempotencyKey, hasBody), ...options.headers },
      body: hasBody ? JSON.stringify(options.body) : null,
      signal: options.signal ?? null,
    });
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === "AbortError") throw cause;
    throw new ApiRequestError({
      status: 0,
      code: "network",
      message: "Could not reach the API. Check your connection and try again.",
      retryable: true,
    });
  }
  if (!response.ok) throw await toApiError(response);
  if (response.status === 204) return undefined as T;
  const text = await response.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

/**
 * POST with upload progress (XMLHttpRequest), used for PRD files. Same headers and error
 * model as `api`.
 */
export function apiUpload<T>(
  path: string,
  body: unknown,
  options: { idempotencyKey?: string; onUploadProgress?: (fraction: number) => void; signal?: AbortSignal } = {},
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", buildUrl(path));
    xhr.withCredentials = true;
    for (const [k, v] of Object.entries(requestHeaders("POST", options.idempotencyKey, true))) xhr.setRequestHeader(k, v);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && options.onUploadProgress) options.onUploadProgress(e.loaded / e.total);
    };
    xhr.onload = () => {
      let payload: unknown = null;
      try {
        payload = xhr.responseText ? JSON.parse(xhr.responseText) : null;
      } catch {
        payload = null;
      }
      if (xhr.status >= 200 && xhr.status < 300) resolve(payload as T);
      else reject(parseErrorPayload(xhr.status, payload));
    };
    const networkError = () =>
      new ApiRequestError({
        status: 0,
        code: "network",
        message: "The upload was interrupted. Your content was not lost; try again.",
        retryable: true,
        details: { upload: true },
      });
    xhr.onerror = () => reject(networkError());
    xhr.ontimeout = () => reject(networkError());
    // Some network failures only surface as loadend with status 0.
    xhr.onloadend = () => {
      if (xhr.status === 0) reject(networkError());
    };
    options.signal?.addEventListener("abort", () => xhr.abort());
    xhr.onabort = () => reject(new DOMException("Aborted", "AbortError"));
    xhr.send(JSON.stringify(body));
  });
}

export function isApiError(e: unknown): e is ApiRequestError {
  return e instanceof ApiRequestError;
}
